import { StatusFormData } from "../components/add-edit-stage/components/AddStatusAndSubStatus";
import { RollbackObj } from "../stores/addStageSlice";

export const transformStatusesForBackend = (
  data: StatusFormData | undefined,
  qualifyingName?: string,
  defaultQualSub?: any,
  rejectionNames?: string[],
  rollback?: RollbackObj,
  /** Sub-status-level rejections as `"statusName::subName"` composite keys
   *  (sub names are unique within a status, so the pair is unambiguous). */
  rejectionSubKeys?: string[]
) => {
  if (!Array.isArray(data)) return [];

  const hasQualifying = !!qualifyingName?.trim();
  const rejectionSet = new Set(
    (rejectionNames ?? []).filter((n): n is string => !!n && !!n.trim())
  );
  const rejectionSubSet = new Set(
    (rejectionSubKeys ?? []).filter((k): k is string => !!k && !!k.trim())
  );
  const hasRollback = !!rollback?.parentId;
  const hasQualSub = !!defaultQualSub?.subId;

  return data.map((status, index) => {
    // Toggle gate: when the status has the Preferences toggle OFF, send an
    // empty list regardless of any in-memory rows. This makes the toggle
    // authoritative — flipping it off and saving clears the persisted
    // rules, even if the user had previously filled rows.
    const enabled = !!(status as any)?.preferencesEnabled;

    // Drop half-filled preference rows (the trailing "add next" row keeps
    // both ids null until the user picks something) and de-dupe by
    // (customInputId, customInputItemId) — the server's unique index would
    // reject dupes anyway, but a friendlier failure mode is to silently
    // collapse them client-side.
    const rawPrefs =
      enabled && Array.isArray((status as any)?.preferences)
        ? (status as any).preferences
        : [];
    const seenPrefKeys = new Set<string>();
    const preferences = rawPrefs
      .filter(
        (p: any) =>
          Number.isFinite(p?.customInputId) &&
          Number.isFinite(p?.customInputItemId),
      )
      .filter((p: any) => {
        const k = `${p.customInputId}:${p.customInputItemId}`;
        if (seenPrefKeys.has(k)) return false;
        seenPrefKeys.add(k);
        return true;
      })
      .map((p: any) => ({
        customInputId: p.customInputId,
        customInputItemId: p.customInputItemId,
      }));

    return {
      id: status?.id,
      name: status?.statusName ?? "",
      order: index + 1,

      isQualifying: hasQualifying && status?.statusName === qualifyingName,
      isRejection: !!status?.statusName && rejectionSet.has(status.statusName),
      isRollback: hasRollback && status?.id === rollback?.parentId,

      // "Follow-up needed" — absent on the card (add flow / untouched
      // rows) means ON; only an explicit false disables. `followUpDays`
      // null = inherit the stage's value.
      showInFollowups: (status as any)?.showInFollowups !== false,
      followUpDays: (status as any)?.followUpDays ?? null,
      maxFollowUpDays: (status as any)?.maxFollowUpDays ?? null,
      // "Prioritise latest deal" (rule 9). Absent on the card = OFF.
      followUpLatestDealOnly:
        (status as any)?.followUpLatestDealOnly === true,

      // "Sequence wise" — the lead/deal pickers offer the sub-statuses one at
      // a time, in order (Status.isSequential). Absent on the card = OFF.
      isSequential: status?.isSequential === true,

      subStatuses: Array.isArray(status?.subStatuses)
        ? status.subStatuses.map((sub) => ({
            id: sub?.id,
            name: sub?.name ?? "",

            isDefaultRollback:
              hasRollback && !!rollback?.subId && sub?.id === rollback?.subId,

            isDefaultQualifying:
              hasQualifying &&
              hasQualSub &&
              status?.statusName === qualifyingName &&
              sub?.id === defaultQualSub?.subId,

            // Sub-status-level rejection mark (two-level rejection).
            isRejection:
              !!sub?.name &&
              rejectionSubSet.has(`${status?.statusName}::${sub.name}`),

            showInFollowups: (sub as any)?.showInFollowups !== false,
            followUpDays: (sub as any)?.followUpDays ?? null,
            maxFollowUpDays: (sub as any)?.maxFollowUpDays ?? null,
            followUpLatestDealOnly:
              (sub as any)?.followUpLatestDealOnly === true,
          }))
        : [],

      preferences,
    };
  });
};
